import React, { useCallback, useEffect, useRef } from 'react';
import { Form, Input, Select } from 'antd';
import { useTranslation } from 'react-i18next';
import { T, SECTION_LABEL, FIELD_HINT } from './declarationTheme';
import { Field, Grid2, inputProps, selectProps } from './fields';
import { QUALITES_DECLARANT } from '../../../variables/constants';

/* Qualités dont les coordonnées se déduisent de ce qui est déjà saisi. */
const QUALITES_PRE_REMPLIES = ['ASSURE', 'CONDUCTEUR', 'BUREAU_HOMOLOGUE'];

/* Bloc compact "Déclaration effectuée par" - affiché sur l'étape Récap.
   On choisit d'abord la qualité du déclarant ; si elle correspond à une
   personne ou un organisme déjà renseigné dans le formulaire (assuré,
   conducteur, bureau homologue), ses coordonnées sont importées automatiquement. */
export default function DeclarantBlock({ form, organismes = [] }) {
  const { t } = useTranslation('declaration');
  /* `preserve: true` n'est PAS un détail : sans lui, rien n'est jamais prérempli.
     `Form.useWatch` lit `getFieldsValue()`, qui ne rend que les champs MONTÉS -
     et ce bloc s'affiche à l'étape Récapitulatif, alors que l'assuré (étape 2) et
     le conducteur (étape 2) sont démontés depuis longtemps. Les six valeurs
     ci-dessous revenaient donc toutes `undefined`, `sourceFor` ne trouvait aucune
     source, et le garde-fou « source vide » coupait le pré-remplissage.
     Avec l'option, useWatch lit `getFieldsValue(true)` - le magasin complet, que
     le `preserve` du <Form> maintient. C'est déjà ce que fait StepRecapitulatif
     pour ses `values`, et c'est pour la même raison. */
  const surTout = { form, preserve: true };
  const qualite = Form.useWatch('declarantQualite', surTout);
  const assureNom = Form.useWatch('assureNom', surTout);
  const assurePrenom = Form.useWatch('assurePrenom', surTout);
  const assureTel = Form.useWatch('assureTelephone', surTout);
  const conducteurEstAssure = Form.useWatch('conducteurEstAssure', surTout);
  const conducteurNom = Form.useWatch('conducteurNom', surTout);
  const conducteurPrenom = Form.useWatch('conducteurPrenom', surTout);
  const bureauHomologueId = Form.useWatch('organismeHomologueTrackingId', surTout);

  /* Dernières valeurs importées automatiquement - pour ne jamais écraser
     ou effacer une saisie manuelle. */
  const lastAutoFill = useRef(null);

  const sourceFor = useCallback((q) => {
    if (q === 'ASSURE') {
      return { nom: assureNom, prenom: assurePrenom, telephone: assureTel };
    }
    if (q === 'CONDUCTEUR') {
      // Conducteur non distingué de l'assuré : ses coordonnées SONT celles de l'assuré.
      return conducteurEstAssure !== false
        ? { nom: assureNom, prenom: assurePrenom, telephone: assureTel }
        : { nom: conducteurNom, prenom: conducteurPrenom, telephone: undefined };
    }
    if (q === 'BUREAU_HOMOLOGUE') {
      // Le bureau désigné à l'étape Contexte : un organisme, donc ni prénom ni personne.
      const bureau = organismes.find(o => o.organismeTrackingId === bureauHomologueId);
      return bureau
        ? { nom: bureau.raisonSociale, prenom: undefined, telephone: bureau.telephonePrincipal || undefined }
        : null;
    }
    return null;
  }, [assureNom, assurePrenom, assureTel, conducteurEstAssure, conducteurNom, conducteurPrenom,
    organismes, bureauHomologueId]);

  const currentMatchesAutoFill = useCallback(() => {
    const last = lastAutoFill.current;
    if (!last) return false;
    const v = form.getFieldsValue(['declarantNom', 'declarantPrenom', 'declarantTelephone']);
    return (
      (v.declarantNom || '') === (last.nom || '') &&
      (v.declarantPrenom || '') === (last.prenom || '') &&
      (v.declarantTelephone || '') === (last.telephone || '')
    );
  }, [form]);

  /* Le pré-remplissage ne peut pas dépendre du seul `onChange` du select :
     il doit aussi jouer à l'affichage de l'étape (brouillon restauré, dossier
     en modification, retour arrière) et quand la source change après coup -
     l'assuré corrigé à l'étape 2 doit se répercuter ici.

     Deux garde-fous : on ne remplit que si le bloc est vide OU s'il contient
     exactement ce qu'on y avait mis. Une saisie manuelle n'est jamais écrasée. */
  useEffect(() => {
    if (!QUALITES_PRE_REMPLIES.includes(qualite)) return;
    const src = sourceFor(qualite);
    if (!src || (!src.nom && !src.prenom && !src.telephone)) return;

    const v = form.getFieldsValue(['declarantNom', 'declarantPrenom', 'declarantTelephone']);
    const vide = !v.declarantNom && !v.declarantPrenom && !v.declarantTelephone;
    if (!vide && !currentMatchesAutoFill()) return;

    form.setFieldsValue({
      declarantNom: src.nom,
      declarantPrenom: src.prenom,
      declarantTelephone: src.telephone,
    });
    lastAutoFill.current = src;
  }, [qualite, sourceFor, currentMatchesAutoFill, form]);

  const onQualiteChange = (q) => {
    const src = sourceFor(q);
    const peutPreRemplir = !!src && !!(src.nom || src.prenom || src.telephone);
    /* On quitte une qualité pré-remplie pour une qualité qui ne l'est pas :
       on repart d'un bloc vierge plutôt que de laisser les coordonnées de
       l'assuré sous une qualité « témoin » ou « proche ». */
    if (!peutPreRemplir && currentMatchesAutoFill()) {
      form.setFieldsValue({
        declarantNom: undefined,
        declarantPrenom: undefined,
        declarantTelephone: undefined,
      });
      lastAutoFill.current = null;
    }
  };

  const autoFilled = QUALITES_PRE_REMPLIES.includes(qualite) && currentMatchesAutoFill();

  return (
    <div
      style={{
        marginTop: 32,
        padding: '20px 22px',
        background: T.paper,
        border: `1px solid ${T.line}`,
        borderRadius: T.r.md,
      }}
    >
      <div style={{ marginBottom: 4 }}>
        <div style={SECTION_LABEL}>{t('declarant.titre')}</div>
      </div>

      <Grid2>
        <Field
          name="declarantQualite"
          label={t('declarant.qualite')}
          rules={[{ required: true, message: t('declarant.qualiteRequise') }]}
        >
          <Select
            {...selectProps}
            placeholder={t('commun.selectionner')}
            optionFilterProp="label"
            onChange={onQualiteChange}
          >
            {QUALITES_DECLARANT.map(q => (
              <Select.Option key={q.value} value={q.value}>{q.label}</Select.Option>
            ))}
          </Select>
        </Field>
        {qualite === 'AUTRE' && (
          <Field name="declarantQualiteDetail" label={t('declarant.precisez')}>
            <Input {...inputProps} placeholder={t('declarant.precisezExemple')} maxLength={150} />
          </Field>
        )}
      </Grid2>

      {autoFilled && (
        <div style={{ ...FIELD_HINT, marginTop: -8, marginBottom: 12 }}>
          {t('declarant.coordonneesImportees')}
        </div>
      )}

      <Grid2>
        <Field
          name="declarantNom"
          label={t('declarant.nom')}
          rules={[{ required: true, message: t('declarant.nomRequis') }]}
        >
          <Input {...inputProps} placeholder={t('declarant.nomExemple')} maxLength={200} />
        </Field>
        <Field name="declarantPrenom" label={t('declarant.prenom')}>
          <Input {...inputProps} placeholder={t('declarant.prenom')} maxLength={200} />
        </Field>
      </Grid2>

      <Grid2>
        <Field name="declarantTelephone" label={t('declarant.telephone')}>
          <Input {...inputProps} placeholder="+228 ..." maxLength={30} />
        </Field>
        <div />
      </Grid2>
    </div>
  );
}
